import type { Address, Hex } from "viem";
import type { PrivateKeyAccount } from "viem/accounts";
import { CONSOLE_IDENTITY, extensionIdentityName, identityAccount, signIdentityRoot } from "@woodpecker/protocol";

/**
 * The identity root of a wallet in this browser (D73, D50a): keccak256 of the wallet's one deterministic signature
 * over the root's typed data, kept beside the wallet key. The Console's own identity and one identity per installed
 * extension derive from it; their keys never leave this origin.
 */
const ROOTS = "woodpecker-identity-root"; // `${chainId}:${wallet}` → root (typed-data roots; the key changed with them)

function readRoots(): Record<string, Hex> {
  try {
    return JSON.parse(localStorage.getItem(ROOTS) ?? "{}") as Record<string, Hex>;
  } catch {
    return {};
  }
}

const keyOf = (chainId: number, wallet: Address) => `${chainId}:${wallet.toLowerCase()}`;

export function rootOf(chainId: number, wallet: Address): Hex | null {
  return readRoots()[keyOf(chainId, wallet)] ?? null;
}

export function saveRoot(chainId: number, wallet: Address, root: Hex): void {
  const all = readRoots();
  all[keyOf(chainId, wallet)] = root;
  localStorage.setItem(ROOTS, JSON.stringify(all));
}

export function forgetRoots(): void {
  localStorage.removeItem(ROOTS);
}

/** Signs the root's typed data with the wallet key (an EOA signs it the same way every time) and keeps the root here. */
export async function deriveRoot(chainId: number, account: PrivateKeyAccount): Promise<Hex> {
  const root = await signIdentityRoot(account, chainId);
  saveRoot(chainId, account.address, root);
  return root;
}

export function consoleIdentityOf(root: Hex): PrivateKeyAccount {
  return identityAccount(root, CONSOLE_IDENTITY);
}

export function extensionIdentityOf(root: Hex, extensionId: string): PrivateKeyAccount {
  return identityAccount(root, extensionIdentityName(extensionId));
}
